import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '../common/Card';
import { Input } from '../common/Input';
import { Button } from '../common/Button';
import { adminApi, ApiKey } from '../../api/admin.api';
import { Plus, Trash2, KeyRound } from 'lucide-react';
import toast from 'react-hot-toast';

export const ApiKeysPanel: React.FC = () => {
  const qc = useQueryClient();

  const keysQuery = useQuery({
    queryKey: ['admin-api-keys'],
    queryFn: () => adminApi.getApiKeys(),
  });

  const createMutation = useMutation({
    mutationFn: (v: { name: string; expiresInDays?: number }) => adminApi.createApiKey(v),
    onSuccess: () => {
      toast.success('API key created');
      qc.invalidateQueries({ queryKey: ['admin-api-keys'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (name: string) => adminApi.deleteApiKey(name),
    onSuccess: () => {
      toast.success('API key deleted');
      qc.invalidateQueries({ queryKey: ['admin-api-keys'] });
    },
  });

  const [name, setName] = React.useState('');
  const [expiresInDays, setExpiresInDays] = React.useState<number | ''>('');

  const keys: ApiKey[] = keysQuery.data?.data || [];

  return (
    <div className="space-y-6">
      <Card title="Create API Key" subtitle="Create a named API key for CI or automation">
        <div className="grid grid-cols-3 gap-4">
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="CI Token" />
          <Input
            label="Expires In (days)"
            type="number"
            value={expiresInDays}
            onChange={(e) => setExpiresInDays(e.target.value ? Number(e.target.value) : '')}
            placeholder="30"
          />
          <div className="flex items-end">
            <Button
              icon={<Plus className="w-4 h-4" />}
              onClick={() => createMutation.mutate({ name, expiresInDays: expiresInDays || undefined })}
              isLoading={createMutation.isPending}
              disabled={!name}
            >
              Create
            </Button>
          </div>
        </div>
        {createMutation.data?.data?.key && (
          <div className="mt-4 p-3 rounded bg-gray-100 text-sm break-all">
            <span className="font-medium">New key:</span> {createMutation.data.data.key}
          </div>
        )}
      </Card>

      <Card title="Existing API Keys" subtitle="Revoke keys that are no longer needed">
        {keysQuery.isLoading ? (
          <p className="text-gray-600">Loading...</p>
        ) : keys.length === 0 ? (
          <p className="text-gray-600">No API keys.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-600 border-b">
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Created</th>
                  <th className="py-2 pr-4">Expires</th>
                  <th className="py-2 pr-4">Last Used</th>
                  <th className="py-2 pr-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.name} className="border-b last:border-0">
                    <td className="py-2 pr-4 font-medium flex items-center gap-2">
                      <KeyRound className="w-4 h-4 text-gray-500" /> {k.name}
                    </td>
                    <td className="py-2 pr-4">{k.createdAt ? new Date(k.createdAt).toLocaleString() : '-'}</td>
                    <td className="py-2 pr-4">{k.expiresAt ? new Date(k.expiresAt).toLocaleDateString() : 'Never'}</td>
                    <td className="py-2 pr-4">{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : '-'}</td>
                    <td className="py-2 pr-4">
                      <Button
                        variant="danger"
                        size="sm"
                        icon={<Trash2 className="w-4 h-4" />}
                        onClick={async () => {
                          // eslint-disable-next-line no-restricted-globals
                          if (!confirm(`Delete API key "${k.name}"?`)) return;
                          await deleteMutation.mutateAsync(k.name);
                        }}
                        isLoading={deleteMutation.isPending}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};